import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { withPrefix } from 'gatsby';
import type { Node as ReactNode } from 'react';
import { useSiteMetadata } from '../../hooks';
import styles from './Layout.module.scss';
import Header from '../Header';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider } from '@material-ui/styles';
import Viewport from '../Viewport';
import Footer from '../Footer';
import theme from '../../theme';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import GatsbyLink from 'gatsby-link';
import { BREADCRUMBS_MAP } from '../../constants/breadcrumbs';
import Cookies from '../Cookies';

type Props = {
  children: ReactNode,
  title: string,
  description?: string,
  socialImage?: string,
  mainPage?: string,
  location?: Object,
  pageTitle?: string,
  hideBreadcrumbs?: boolean,
};

const Layout = ({
  children,
  title,
  description,
  socialImage,
  mainPage,
  location,
  pageTitle = '',
  hideBreadcrumbs,
  darkBreadcrumbs,
}: Props) => {
  const { author, url } = useSiteMetadata();
  const [showCookiesBanner, setShowCookiesBanner] = useState(false);
  const metaImage = socialImage != null ? socialImage : author.photo;
  const metaImageUrl = url + withPrefix(metaImage);
  const getPaths = () => {
    const paths = location && location.pathname.split('/').filter(p => !!p);
    const lastPartOfPath = path => path.split('/')[path.split('/').length - 1];
    return paths
      .map((p, ind) => paths.slice(0, ind + 1).join('/'))
      .filter(
        p =>
          lastPartOfPath(p) !== 'category' &&
          lastPartOfPath(p) !== 'tag' &&
          lastPartOfPath(p) !== 'services' &&
          lastPartOfPath(p) !== 'expertise'
      );
  };
  const pathsResult = getPaths();
  let index;
  useEffect(() => {
    try {
      if(localStorage.getItem('isCookieBannerClosed') !== null){
        setTimeout(() =>{
          const hubspotCookie = document.cookie.split(';').find(str => {
            const match = str.match(/hubspotutk/) || str.match(/__hs_initial_opt_in/);
            return match && !!match?.input.split('=')[1];
          });
          localStorage.setItem('isCookieBannerClosed', !!hubspotCookie);
          setShowCookiesBanner(!JSON.parse(localStorage.getItem('isCookieBannerClosed')));
        }, 0)
      } else {
        setShowCookiesBanner(true);
      }

    } catch (e) {
    }
  }, []);

  const onCookiesBannerClose = () => {
    if (document.getElementById('hs-eu-confirmation-button') !== null) {
      document.getElementById('hs-eu-confirmation-button').click();
    }

    setShowCookiesBanner(false);
    localStorage.setItem('isCookieBannerClosed', true);
  };

  return (
    <>
      <Viewport />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className={styles.layout}>
          <Helmet>
            <html lang="en" />
            <title>{title}</title>
            {index && <meta name="robots" content="noindex"/>}
            <meta name="description" content={description} data-react-helmet="true" />
            <meta property="og:site_name" content={title} />
            <meta property="og:description" content={description} data-react-helmet="true"/>
            <meta name="image" property="og:image" content={metaImageUrl} />
            <meta property="og:image:secure_url" content={metaImageUrl} />
            <meta property="og:image:width" content={1280} />
            <meta property="og:image:height" content={640} />
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} data-react-helmet="true" />
            <meta name="twitter:image" content={metaImageUrl} />
            <link
              href="https://fonts.googleapis.com/css2?family=Red+Hat+Text:wght@400;500;700&display=swap"
              rel="stylesheet"
            />
            <link
              href="https://fonts.googleapis.com/icon?family=Material+Icons"
              rel="stylesheet"
            />
            <meta name="google-site-verification" content="4mRv4anl8iIWjjwT5ONW8A9hdIv0EWawyo_T_pMwZYA" />
          </Helmet>
          <Header mainPage={mainPage} location={location} />
          <div className={styles['layout__body']}>
            {pathsResult && pathsResult.length > 0 && !hideBreadcrumbs && (
              <Breadcrumbs
                separator={'>'}
                className={darkBreadcrumbs ? styles['dark'] : ''}
              >
                <GatsbyLink to="/">HOME</GatsbyLink>
                {pathsResult.map((path, ind) => (
                  <GatsbyLink
                    key={path}
                    to={`/${path}`}
                    className={
                      ind === pathsResult.length - 1 ? styles['active'] : ''
                    }
                  >
                    {BREADCRUMBS_MAP[path] ? BREADCRUMBS_MAP[path] : pageTitle}
                  </GatsbyLink>
                ))}
              </Breadcrumbs>
            )}
            {children}
            {showCookiesBanner && <Cookies onClose={onCookiesBannerClose} />}
          </div>
          <Footer />
        </div>
      </ThemeProvider>
    </>
  );
};

export default Layout;
